import {
  ArrowLeftIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import cscLogo from '../assets/CSC2025.png';
import { config, configUtils } from '../config/env';

/**
 * Validation schema for the CSC Live interest form
 */
const cscInterestSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  organization: z.string().min(2, 'Organization name is required'),
  phone: z.string().optional(),
  organizationType: z.enum([
    'school',
    'npa',
    'therapy-company',
    'cooperative',
    'other',
  ]),
  message: z.string().min(10, 'Please provide more details about your needs'),
  studentCount: z.string().optional(),
});

/**
 * Form data type derived from validation schema
 */
type CscInterestFormData = z.infer<typeof cscInterestSchema>;

/**
 * Form submission error interface
 */
interface SubmissionError {
  message: string;
  field?: string;
  code?: string;
}

/**
 * Form submission state enum
 */
type SubmissionState = 'idle' | 'submitting' | 'success' | 'error';

/**
 * CSC Live Conference interest form page
 *
 * @component
 */
const CscLivePage: React.FC = () => {
  const [submissionState, setSubmissionState] =
    useState<SubmissionState>('idle');
  const [submissionError, setSubmissionError] =
    useState<SubmissionError | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CscInterestFormData>({
    resolver: zodResolver(cscInterestSchema),
  });

  const onSubmit = async (data: CscInterestFormData) => {
    setSubmissionState('submitting');
    setSubmissionError(null);

    try {
      const submissionUrl = configUtils.getContactFormSubmissionUrl();

      // Add CSC Live context to the submission
      const submissionData = {
        ...data,
        message: `[CSC Live Conference Interest]\n\n${data.message}`,
      };

      configUtils.debugLog('Submitting CSC Live interest form:', submissionData);
      configUtils.debugLog('Using submission URL:', submissionUrl);

      const response = await fetch(submissionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
            `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const result = await response.json();
      configUtils.debugLog('CSC Live form submitted successfully:', result);

      setSubmissionState('success');
      reset();
      setRetryCount(0);
    } catch (error) {
      configUtils.debugLog('CSC Live form submission error:', error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while submitting the form';

      // Check for specific error types
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setSubmissionError({
          message:
            'Unable to connect to the server. Please check your internet connection and try again.',
          code: 'NETWORK_ERROR',
        });
      } else if (errorMessage.includes('400')) {
        setSubmissionError({
          message: 'Please check your information and try again.',
          code: 'VALIDATION_ERROR',
        });
      } else if (errorMessage.includes('429')) {
        setSubmissionError({
          message:
            'Too many requests. Please wait a moment before trying again.',
          code: 'RATE_LIMIT_ERROR',
        });
      } else if (errorMessage.includes('500')) {
        setSubmissionError({
          message:
            'Server error. Our team has been notified. Please try again later.',
          code: 'SERVER_ERROR',
        });
      } else {
        setSubmissionError({
          message: errorMessage,
          code: 'UNKNOWN_ERROR',
        });
      }

      setSubmissionState('error');
      setRetryCount(prev => prev + 1);
    }
  };

  const resetForm = () => {
    setSubmissionState('idle');
    setSubmissionError(null);
    setRetryCount(0);
  };

  // Success state
  if (submissionState === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
        <div className="container-padding section-padding">
          <div className="max-w-2xl mx-auto">
            {/* Header with logo */}
            <div className="text-center mb-8">
              <img
                src={cscLogo}
                alt="CSC Live 2025"
                className="h-24 mx-auto mb-6"
              />
            </div>

            {/* Success message */}
            <div className="card text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircleIcon className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-secondary-900 mb-4">
                Thank You for Your Interest!
              </h2>
              <p className="text-xl text-secondary-600 mb-8">
                We're excited to connect with you about TherapyLink! We'll follow
                up shortly with more details and schedule a personalized demo.
              </p>
              <div className="space-y-4">
                <button onClick={resetForm} className="btn-secondary w-full sm:w-auto">
                  Submit Another Response
                </button>
                <div className="text-center">
                  <Link
                    to="/"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
                  >
                    <ArrowLeftIcon className="w-4 h-4 mr-2" />
                    Return to TherapyLink Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      <div className="container-padding section-padding">
        <div className="max-w-4xl mx-auto">
          {/* Header with logo and intro */}
          <div className="text-center mb-12">
            <img
              src={cscLogo}
              alt="CSC Live 2025"
              className="h-24 mx-auto mb-6"
            />
            <h1 className="text-4xl sm:text-5xl font-bold text-secondary-900 mb-4">
              Welcome CSC Live Attendees!
            </h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">
              Great meeting you at the conference! Let's continue the conversation
              about how TherapyLink can transform your therapy service operations.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sidebar - What to expect */}
            <div className="lg:col-span-1">
              <div className="card sticky top-8">
                <h3 className="text-xl font-bold text-secondary-900 mb-4">
                  What's Next?
                </h3>
                <ul className="space-y-3 text-secondary-600">
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span>We'll reach out within 24 hours</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span>Schedule a personalized demo</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span>Discuss your specific needs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span>Review implementation timeline</span>
                  </li>
                </ul>

                <div className="mt-6 pt-6 border-t border-secondary-200">
                  <h4 className="font-semibold text-secondary-900 mb-3">
                    Quick Contact
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center text-sm">
                      <EnvelopeIcon className="w-4 h-4 text-primary-600 mr-2" />
                      <a
                        href={`mailto:${config.contactEmail}`}
                        className="hover:text-primary-600 transition-colors duration-200"
                      >
                        {config.contactEmail}
                      </a>
                    </div>
                    <div className="flex items-center text-sm">
                      <PhoneIcon className="w-4 h-4 text-primary-600 mr-2" />
                      <a
                        href={`tel:${config.salesPhone.replace(/\s/g, '')}`}
                        className="hover:text-primary-600 transition-colors duration-200"
                      >
                        {config.salesPhone}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-secondary-200">
                  <Link
                    to="/"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200 text-sm"
                  >
                    <ArrowLeftIcon className="w-4 h-4 mr-2" />
                    Visit TherapyLink.org
                  </Link>
                </div>
              </div>
            </div>

            {/* Main form */}
            <div className="lg:col-span-2">
              <div className="card">
                <h2 className="text-2xl font-bold text-secondary-900 mb-6">
                  Share Your Information
                </h2>

                {/* Error Display */}
                {submissionState === 'error' && submissionError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-red-400 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Submission Failed
                        </h3>
                        <p className="text-sm text-red-700 mt-1">
                          {submissionError.message}
                        </p>
                        {retryCount >= 3 && (
                          <p className="text-sm text-red-600 mt-2">
                            If the problem persists, please contact us directly at{' '}
                            <a
                              href={`mailto:${config.contactEmail}`}
                              className="underline hover:text-red-800"
                            >
                              {config.contactEmail}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-secondary-700 mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        {...register('name')}
                        type="text"
                        id="name"
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                        placeholder="Your full name"
                        aria-required="true"
                        aria-invalid={errors.name ? 'true' : 'false'}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                      />
                      {errors.name && (
                        <p id="name-error" className="text-red-600 text-sm mt-1" role="alert">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-secondary-700 mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        {...register('email')}
                        type="email"
                        id="email"
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                        placeholder="your.email@organization.edu"
                        aria-required="true"
                        aria-invalid={errors.email ? 'true' : 'false'}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                      />
                      {errors.email && (
                        <p id="email-error" className="text-red-600 text-sm mt-1" role="alert">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="organization"
                        className="block text-sm font-medium text-secondary-700 mb-2"
                      >
                        Organization Name *
                      </label>
                      <input
                        {...register('organization')}
                        type="text"
                        id="organization"
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                        placeholder="Organization Name"
                      />
                      {errors.organization && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.organization.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-secondary-700 mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        {...register('phone')}
                        type="tel"
                        id="phone"
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="organizationType"
                        className="block text-sm font-medium text-secondary-700 mb-2"
                      >
                        Organization Type *
                      </label>
                      <select
                        {...register('organizationType')}
                        id="organizationType"
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      >
                        <option value="">Select type...</option>
                        <option value="school">School</option>
                        <option value="npa">NPA</option>
                        <option value="therapy-company">Therapy Company</option>
                        <option value="cooperative">Education Cooperative</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.organizationType && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.organizationType.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="studentCount"
                        className="block text-sm font-medium text-secondary-700 mb-2"
                      >
                        Approximate Student Count
                      </label>
                      <select
                        {...register('studentCount')}
                        id="studentCount"
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      >
                        <option value="">Select range...</option>
                        <option value="0-500">0-500 students</option>
                        <option value="500-2000">500-2,000 students</option>
                        <option value="2000-10000">2,000-10,000 students</option>
                        <option value="10000+">10,000+ students</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-secondary-700 mb-2"
                    >
                      Tell Us About Your Needs *
                    </label>
                    <textarea
                      {...register('message')}
                      id="message"
                      rows={4}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="What are your current challenges? What would you like to learn more about?"
                      aria-required="true"
                      aria-invalid={errors.message ? 'true' : 'false'}
                      aria-describedby={errors.message ? 'message-error' : undefined}
                    />
                    {errors.message && (
                      <p id="message-error" className="text-red-600 text-sm mt-1" role="alert">
                        {errors.message.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submissionState === 'submitting'}
                    className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    aria-busy={submissionState === 'submitting'}
                  >
                    {submissionState === 'submitting' ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending...
                      </>
                    ) : submissionState === 'error' && retryCount > 0 ? (
                      `Try Again (${retryCount + 1})`
                    ) : (
                      'Submit Interest Form'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CscLivePage;
